import { Box, Typography } from "@mui/material";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import { useState } from "react";
import {
  HOME_GLASS_BACKDROP,
  homeGlassHero,
} from "../../../pages/Home/constants/homeGlass";

export default function UploadFileDropZone({
  inputId,
  file,
  files,
  hasError = false,
  uploading = false,
  multiple = true,
  accept,
  onFileSelect,
  browseText = "Choose File",
  changeFileText = "Choose Another",
  emptyTitle = "Drag files to upload,or",
  dragTitle = "Release to attach",
  selectedTitle = "File ready to upload",
}) {
  const [dragActive, setDragActive] = useState(false);
  const selectedFiles = Array.isArray(files) ? files : file ? [file] : [];
  const hasFile = selectedFiles.length > 0;
  const selectedFileText =
    selectedFiles.length <= 2
      ? selectedFiles.map((selectedFile) => selectedFile.name).join(", ")
      : `${selectedFiles.length} files selected`;
  const selectedFileTitle = selectedFiles
    .map((selectedFile) => selectedFile.name)
    .join("\n");

  const normalizeSelectedFiles = (nextSelection) => {
    if (!nextSelection) return [];
    if (Array.isArray(nextSelection)) return nextSelection.filter(Boolean);
    if (typeof FileList !== "undefined" && nextSelection instanceof FileList) {
      return Array.from(nextSelection).filter(Boolean);
    }
    return [nextSelection].filter(Boolean);
  };

  const handleFileSelect = (nextSelection) => {
    if (uploading) return;
    const nextFiles = normalizeSelectedFiles(nextSelection);
    onFileSelect?.(multiple ? nextFiles : nextFiles[0] ?? null);
  };

  const handleInputChange = (event) => {
    handleFileSelect(event.target.files);
    event.target.value = "";
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    if (uploading) return;
    event.dataTransfer.dropEffect = "copy";
    setDragActive(true);
  };

  const handleDragLeave = (event) => {
    if (uploading) return;
    if (event.currentTarget.contains(event.relatedTarget)) return;
    setDragActive(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    if (uploading) return;
    setDragActive(false);
    handleFileSelect(event.dataTransfer.files);
  };

  return (
    <Box
      component="label"
      htmlFor={inputId}
      onDragEnter={handleDragOver}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      sx={{
        "@keyframes uploadPulse": {
          "0%, 100%": { boxShadow: "0 10px 28px rgba(219, 165, 41, 0.22)" },
          "50%": {
            boxShadow:
              "0 10px 28px rgba(219, 165, 41, 0.34), 0 0 0 9px rgba(219, 165, 41, 0.08)",
          },
        },
        "@keyframes uploadIconBob": {
          "0%, 100%": { transform: "translateY(0)" },
          "50%": { transform: "translateY(-5px)" },
        },
        "@keyframes uploadButtonShine": {
          "0%": { transform: "translateX(-120%) skewX(-18deg)" },
          "100%": { transform: "translateX(220%) skewX(-18deg)" },
        },
        "@keyframes uploadCheckPop": {
          "0%": { opacity: 0, transform: "scale(0.55) rotate(-16deg)" },
          "62%": { opacity: 1, transform: "scale(1.12) rotate(0deg)" },
          "100%": { opacity: 1, transform: "scale(1) rotate(0deg)" },
        },
        "@keyframes uploadRingSpin": {
          "0%": { transform: "rotate(0deg)" },
          "100%": { transform: "rotate(360deg)" },
        },
        "@keyframes uploadFileFloat": {
          "0%, 100%": { transform: "translateY(0) scale(1)" },
          "50%": { transform: "translateY(-3px) scale(1.02)" },
        },
        position: "relative",
        isolation: "isolate",
        width: "min(420px, 100%)",
        minHeight: { xs: 196, sm: 212 },
        mb: hasError ? 1.5 : 0,
        borderRadius: "20px",
        background: dragActive
          ? "linear-gradient(135deg, rgba(255, 255, 255, 0.32), rgba(219, 165, 41, 0.16))"
          : homeGlassHero.rest,
        backdropFilter: HOME_GLASS_BACKDROP,
        WebkitBackdropFilter: HOME_GLASS_BACKDROP,
        border: dragActive
          ? "1px solid rgba(255, 255, 255, 0.34)"
          : "1px solid rgba(255, 255, 255, 0.12)",
        boxShadow: dragActive
          ? "inset 0 1px 0 rgba(255,255,255,0.26), 0 20px 48px rgba(0,0,0,0.24), 0 0 0 5px rgba(219, 165, 41, 0.08)"
          : "inset 0 1px 0 rgba(255,255,255,0.22), 0 18px 40px rgba(0,0,0,0.16)",
        p: { xs: 3, sm: 3.2 },
        cursor: "pointer",
        pointerEvents: uploading ? "none" : "auto",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 1.8,
        overflow: "hidden",
        transition:
          "border-color 180ms ease, box-shadow 180ms ease, transform 180ms ease",
        transform: dragActive ? "translateY(-4px) scale(1.01)" : "translateY(0)",
        "&:hover": {
          background: homeGlassHero.hover,
          borderColor: "rgba(255, 255, 255, 0.22)",
          boxShadow:
            "inset 0 1px 0 rgba(255,255,255,0.26), 0 22px 52px rgba(0,0,0,0.22), 0 0 0 4px rgba(219, 165, 41, 0.06)",
          transform: dragActive ? "translateY(-4px) scale(1.01)" : "translateY(-2px)",
        },
        "&:focus-within": {
          borderColor: "rgba(255, 255, 255, 0.28)",
          boxShadow:
            "inset 0 1px 0 rgba(255,255,255,0.26), 0 22px 52px rgba(0,0,0,0.22), 0 0 0 4px rgba(219, 165, 41, 0.08)",
        },
      }}
    >
      <input
        type="file"
        id={inputId}
        hidden
        disabled={uploading}
        multiple={multiple}
        accept={accept}
        onChange={handleInputChange}
      />
      <Box
        aria-hidden
        sx={{
          position: "relative",
          zIndex: 1,
          width: 62,
          height: 62,
          borderRadius: "18px",
          display: "grid",
          placeItems: "center",
          background: "linear-gradient(145deg, #f0c533, #b98211)",
          border: "1px solid rgba(255,255,255,0.2)",
          boxShadow: "0 10px 28px rgba(219, 165, 41, 0.22)",
          animation: dragActive
            ? "uploadPulse 1.2s ease-in-out infinite"
            : hasFile && !uploading
              ? "uploadPulse 1.6s ease-out 1"
              : "none",
          transition: "box-shadow 180ms ease, transform 180ms ease",
          transform: dragActive ? "scale(1.04)" : "scale(1)",
        }}
      >
        {uploading ? (
          <>
            <Box
              aria-hidden
              sx={{
                position: "absolute",
                inset: -3,
                borderRadius: "20px",
                border: "3px solid rgba(255, 255, 255, 0.2)",
                borderRightColor: "#f3c94a",
                borderBottomColor: "#f3c94a",
                animation: "uploadRingSpin 1s linear infinite",
              }}
            />
            <InsertDriveFileOutlinedIcon
              sx={{
                position: "relative",
                fontSize: 32,
                color: "#fff",
                animation: "uploadFileFloat 1.2s ease-in-out infinite",
              }}
            />
          </>
        ) : hasFile ? (
          <CheckRoundedIcon
            sx={{
              fontSize: 36,
              color: "#fff",
              animation: "uploadCheckPop 420ms cubic-bezier(0.22, 1, 0.36, 1)",
            }}
          />
        ) : (
          <CloudUploadOutlinedIcon
            sx={{
              fontSize: 34,
              color: "#fff",
              animation: dragActive ? "uploadIconBob 900ms ease-in-out infinite" : "none",
            }}
          />
        )}
      </Box>

      <Box sx={{ position: "relative", zIndex: 1, minWidth: 0, width: "100%" }}>
        <Typography
          sx={{
            fontSize: 17,
            color: "#fff",
            fontWeight: 700,
            lineHeight: 1.25,
          }}
        >
          {uploading
            ? "Uploading files..."
            : dragActive
              ? dragTitle
              : hasFile
                ? selectedTitle
                : emptyTitle}
        </Typography>
      </Box>

      <Box
        component="span"
        sx={{
          position: "relative",
          zIndex: 1,
          minWidth: 0,
          px: 0,
          py: 0,
          borderRadius: 0,
          overflow: "hidden",
          color: "rgba(255, 255, 255, 0.58)",
          background: "transparent",
          boxShadow: "none",
          fontSize: 15,
          fontWeight: 500,
          lineHeight: 1.35,
          textAlign: "center",
          transition: "color 180ms ease",
          "&::after": {
            display: "none",
          },
          "&:hover": {
            color: "rgba(255, 255, 255, 0.76)",
          },
        }}
      >
        {uploading ? "Uploading..." : hasFile ? changeFileText : browseText}
      </Box>

      {hasFile ? (
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            mt: 0.15,
            maxWidth: "100%",
            px: 1.25,
            py: 0.7,
            borderRadius: "999px",
            display: "inline-flex",
            alignItems: "center",
            gap: 0.7,
            background: "rgba(255,255,255,0.09)",
            border: "1px solid rgba(255,255,255,0.14)",
            color: "rgba(255,255,255,0.78)",
          }}
        >
          <InsertDriveFileOutlinedIcon sx={{ fontSize: 15, flexShrink: 0 }} />
          <Typography
            component="span"
            title={selectedFileTitle}
            sx={{
              minWidth: 0,
              fontSize: 12,
              fontWeight: 600,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {selectedFileText}
          </Typography>
        </Box>
      ) : null}
    </Box>
  );
}
